/**
 * Hook that loads the {@link VariableContext} used by template variable
 * substitution. Uses the practitioner's linked Organization for
 * contact fields, and loads supervisor credentials when applicable.
 */
import { useEffect, useMemo, useState } from 'react';
import { useMedplum, useMedplumProfile } from '@medplum/react-hooks';
import type { ContactPoint, Organization } from '@medplum/fhirtypes';

import { usePortalSubject } from '../../contexts/PortalSubjectContext';
import { resolvePracticeOrganization } from '../../lib/practiceOrganization';
import type { VariableContext } from './registry';
import { loadSupervisorContextForPractitioner } from './practitionerContext';

function pickContact(
  contacts: ContactPoint[] | undefined,
  system: ContactPoint['system'],
): string | undefined {
  return contacts?.find((c) => c.system === system && c.value)?.value;
}

function organizationToContext(org: Organization | undefined): VariableContext {
  if (!org) return {};
  return {
    organization: {
      name: org.name,
      phone: pickContact(org.telecom, 'phone'),
      email: pickContact(org.telecom, 'email'),
      website: pickContact(org.telecom, 'url'),
    },
  };
}

export interface UseVariableContextOptions {
  /** Load supervisor variables from this provider (e.g. Task assigner on client portal). */
  assignerPractitionerId?: string;
}

export interface UseVariableContextResult {
  context: VariableContext;
  loading: boolean;
  error: Error | null;
}

export function useVariableContext(
  options: UseVariableContextOptions = {},
): UseVariableContextResult {
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const { activePatientId } = usePortalSubject();
  const { assignerPractitionerId } = options;

  const [context, setContext] = useState<VariableContext>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const org = await resolvePracticeOrganization(medplum, profile ?? null, {
          portalPatientId: activePatientId,
        });

        let nextContext = organizationToContext(org ?? undefined);

        if (assignerPractitionerId) {
          try {
            const assigner = await medplum.readResource('Practitioner', assignerPractitionerId);
            const supervisorContext = await loadSupervisorContextForPractitioner(medplum, assigner);
            if (supervisorContext) {
              nextContext = { ...nextContext, supervisor: supervisorContext };
            }
          } catch {
            // Non-fatal: footer variables fall back to placeholders.
          }
        } else {
          const practitioner = profile?.resourceType === 'Practitioner' ? profile : undefined;
          const supervisorContext = await loadSupervisorContextForPractitioner(medplum, practitioner);
          if (supervisorContext) {
            nextContext = { ...nextContext, supervisor: supervisorContext };
          }
        }

        if (!cancelled) setContext(nextContext);
      } catch (err) {
        if (!cancelled) {
          setContext({});
          setError(err instanceof Error ? err : new Error('Failed to load variable context'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [medplum, profile, activePatientId, assignerPractitionerId]);

  return useMemo(() => ({ context, loading, error }), [context, loading, error]);
}
